import { Injectable, ElementRef, NgZone, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, of, pairwise, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { socialAppSelectors } from 'app/modules/social/state/app';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import SocialSettings, { AI_SUMMARIES, MAX_GROUP_SIZE } from 'entity/SocialSettings';
import User from 'entity/User';

import { StageSizeType } from '../performer-participant/performer-participant.types';
import MouseMovementController from '../social-pixi-canvas/mouse-movement-controller';
import Movement from '../social-pixi-canvas/movement';
import { Player } from '../social-pixi-canvas/player';
import { DEFAULT_ZOOM, SceneManager } from '../social-pixi-canvas/scene-manager';

import { ArtifactService } from './artifact/artifact.service';
import { BrowserService } from './browser.service';
import { CTAService } from './cta/cta.service';
import { DeviceService } from './device/device.service';
import { HeaderToastMessageService } from './header-toast-message.service';
import { getPlayerDistance, isPlayerInGroup } from './subscription/subscription.helper';
import { WebsocketService } from './websocket.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarComponent, SnackBarData, SnackBarType } from 'app/shared/snackbar/snackbar.component';
import { PlayerVisibilityService } from '../social-pixi-canvas/player-visibility.service';

export interface VisiblePlayerObject {
  distance: number;
  isInGroup: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SceneService extends Loggable {
  private deviceService = inject(DeviceService);
  private artifactService = inject(ArtifactService);
  private websocketService = inject(WebsocketService);
  private browserService = inject(BrowserService);
  private store = inject<Store<AppState>>(Store);
  private headerToastMessageService = inject(HeaderToastMessageService);
  private ctaService = inject(CTAService);
  private ngZone = inject(NgZone);
  private playerVisibilityService = inject(PlayerVisibilityService);
  private _snackBar = inject(MatSnackBar);

  public sceneManager?: SceneManager;
  public playersInView: Map<string, VisiblePlayerObject> = new Map<string, VisiblePlayerObject>();
  public playersInView$ = new BehaviorSubject<Map<string, VisiblePlayerObject>>(new Map<string, VisiblePlayerObject>());

  public primaryGroupMemberSubject = new BehaviorSubject<Player[]>([]);
  public primaryGroupMembers$ = this.primaryGroupMemberSubject.asObservable();

  private resizeNeeded = new BehaviorSubject<boolean>(false);
  public resizeNeeded$ = this.resizeNeeded.asObservable();

  private groupSoftCap = false;

  private maxGroupBannerId?: number;
  private presentationStageSize?: StageSizeType;
  public isUserPresenting = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private movement: Movement | undefined;
  private mouseMovementController: MouseMovementController | undefined;
  public maxGroupSize?: number;
  private elementRef: ElementRef<HTMLCanvasElement> | undefined;

  private _isLoading = true;
  private _isLoadingRoomTimeout = 2500;
  private socialSettings?: SocialSettings;
  private isHost = false;
  public socialAudioEasingType = 'linear';
  public socialAudioRelative = false;

  public showSagDebuggerStats = false;

  /* TODO: Find a way to overcome the race condition caused by this loading, mainly when joining a room with a active presentation
   *Check the use of isLoading in 'performer.service' file;
   */

  public get isLoading() {
    return this._isLoading;
  }

  // setting to false needs a timeout
  public set isLoading(value) {
    if (value) {
      this._isLoading = value;
      return;
    }
    setTimeout(() => {
      this._isLoading = value;
    }, this._isLoadingRoomTimeout);
  }

  public get isLoadingRoomTimeout() {
    return this._isLoadingRoomTimeout;
  }

  public constructor() {
    super();
    this.setDisplayName('SceneService');
  }

  public init(elementRef: ElementRef): void {
    this.elementRef = elementRef;
    this.destroyed$ = new ReplaySubject(1);
    this.store
      .select(socialAppSelectors.getPresentationStageSize)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((stageSize) => {
        this.presentationStageSize = stageSize;
      });
    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserPresenting) => {
        this.isUserPresenting = isUserPresenting;
      });

    combineLatest([
      this.store.select(socialAppSelectors.getMyGroupScreenShare),
      this.store.select(socialAppSelectors.getFullClusters),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([groupShare]) => {
        if (!this.groupSoftCap) {
          const isPlayerOutsideGroupLimit = !!this.getPlayer()?.groupWarning;
          const { userGroup } = this;
          if (userGroup && isPlayerOutsideGroupLimit && !groupShare) {
            this.displayMaxGroupBanner();
          } else {
            this.removeMaxGroupBanner();
          }
        }
      });

    this.store
      .select(socialAppSelectors.getRoomSettings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialSettings) => {
        this.socialSettings = socialSettings;
        // this state value has moved from settings to setSettings in rooms that have been edited with the scoot home UI
        const maxGroupSize =
          socialSettings?.setSettings?.[MAX_GROUP_SIZE] || socialSettings?.settings?.[MAX_GROUP_SIZE];
        this.maxGroupSize = Number(maxGroupSize);
      });

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => (this.isHost = isHost));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_audio_easing_type)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialAudioEasing) => (this.socialAudioEasingType = socialAudioEasing));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_audio_relative)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialAudioRelative) => (this.socialAudioRelative = socialAudioRelative));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.group_soft_cap)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groupSoftCap) => {
        this.groupSoftCap = groupSoftCap;
      });

    this.store
      .select(socialAppSelectors.showSagDebuggerStats)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showSagDebuggerStats) => {
        this.showSagDebuggerStats = showSagDebuggerStats;
      });

    this.sceneManager = new SceneManager(
      elementRef,
      this,
      this.deviceService,
      this.artifactService,
      this.websocketService,
      this.browserService,
      this.ctaService,
      this.store,
      this.playerVisibilityService,
      this._snackBar,
      this.ngZone,
    );
    this.movement = Movement.getInstance(this.sceneManager);
    this.movement.init(this.websocketService);
    this.mouseMovementController = MouseMovementController.getInstance(this.sceneManager);
    this.mouseMovementController.isMobile = this.browserService.isMobileOrTablet;

    this.store
      .select(socialAppSelectors.getRoomSettings)
      .pipe(takeUntil(this.destroyed$), pairwise())
      .subscribe(([oldSettings, newSettings]) => {
        const oldAiSummariesEnabled = oldSettings?.setSettings?.[AI_SUMMARIES];
        const newAiSummariesEnabled = newSettings?.setSettings?.[AI_SUMMARIES];

        if (oldAiSummariesEnabled === true && newAiSummariesEnabled === false) {
          this.showNotification('info', 'AI Summaries', 'AI Summaries are disabled', 'info');
        }
      });
  }

  /**
   * Get the width of the viewport
   */
  public get screenWidth(): number {
    return this.sceneManager?.viewport?.screenWidth ?? window.innerWidth;
  }

  public getUsers(ids: string[]): User[] {
    return ids.map((id) => this.sceneManager?.getUser(id)).filter(notNil);
  }

  public getUser(id: string): User | undefined {
    return this.sceneManager?.getUser(id);
  }

  /**
   * Hide the pixi player world
   */
  public hidePlayerWorld() {
    this.sceneManager?.hideScene();
  }

  /**
   * Show the pixi player world
   */
  public showPlayerWorld() {
    this.sceneManager?.showScene();
  }

  /**
   * Get a player for a given user id.
   * @param userId The user id of the user for the player to retrieve
   * @returns A player, or undefined if the user is not found.
   */
  public getPlayer(userId?: string): Player | undefined {
    return this.sceneManager?.getPlayer(userId);
  }

  public async playerInView({ player, visible }: { player: Player; visible: boolean }): Promise<void> {
    if (!this.sceneManager) {
      return;
    }
    const { userId } = player;
    if (visible) {
      const distance = await getPlayerDistance(player, this.sceneManager);
      const isInGroup = await isPlayerInGroup(player, this.sceneManager);
      this.playersInView.set(userId, { distance, isInGroup });
    } else if (this.playersInView.has(userId)) {
      this.playersInView.delete(userId);
    }
    this.playersInView$.next(this.playersInView);
  }

  public getPlayersIds(): string[] {
    return [...this.playersInView.keys()];
  }

  public getAllParticipants(): Player[] {
    return [...(this.sceneManager?.participants?.values() ?? [])];
  }

  /**
   * Get the number of users in the room
   * @returns The number of users in the room.
   */
  public getUserCount(): number {
    const participantAndPlayerList = this.sceneManager?.participantsAndPlayerList ?? [];
    return participantAndPlayerList.length;
  }

  /**
   * Get all users in the current room
   * @returns An array of all users.
   */
  public getAllUsers(): User[] {
    const participantAndPlayerList = this.sceneManager?.participantsAndPlayerList ?? [];
    return participantAndPlayerList.map((p) => p.user);
  }

  public get participantCount(): number {
    return this.sceneManager?.participants?.size ?? 0;
  }

  public get participantsAndPlayerList(): Player[] {
    return this.sceneManager?.participantsAndPlayerList ?? [];
  }

  public displayMaxGroupBanner(): void {
    if (!this.groupSoftCap) {
      // If stage size is Medium or Full Screen DO NOT show Max group banner
      // If user is presenting DO NOT show Max group banner
      if (
        this.presentationStageSize === StageSizeType.MEDIUM ||
        this.presentationStageSize === StageSizeType.FULL_SCREEN ||
        this.isUserPresenting
      ) {
        return;
      }
      // Add a replaceable notification, the way server sends group information will
      // trigger this function several times, replace notification to avoid showing
      // multiple notifications.

      this.maxGroupBannerId = this.headerToastMessageService.pushStaticMessage(
        {
          message: `Maximum group size is ${this.maxGroupSize} in this room. A red ring indicates that the group cannot be joined.`,
          buttonText: 'OKAY',
          replace: this.maxGroupBannerId,
        },
        (id) => this.headerToastMessageService.removeMessage(id),
      );
    }
  }

  public removeMaxGroupBanner(): void {
    if (!this.groupSoftCap) {
      this.headerToastMessageService.removeMessage(this.maxGroupBannerId);
      this.maxGroupBannerId = undefined;
    }
  }

  // TODO: DRY this up, resize logic is also in pixi-canvas-component
  public resizeCanvas(): void {
    if (!this.sceneManager) {
      return;
    }
    const newViewportWidth = this.sceneManager.elementRef.nativeElement.offsetWidth;
    const newViewportHeight = this.sceneManager.elementRef.nativeElement.offsetHeight;
    // TODO - Investigate why resize function is not a function sometimes
    // you can see the error in this comment bellow
    // https://github.com/preciate/web/pull/2758#discussion_r1797083289
    if (this.sceneManager.app && this.sceneManager.app.resize) {
      this.sceneManager.app.resize();
    }
    if (this.sceneManager.viewport) {
      this.sceneManager.viewport.resize(newViewportWidth, newViewportHeight);
      this.sceneManager.bounds = this.sceneManager.viewport.getVisibleBounds();
    }

    // Notify
    this.resizeNeeded.next(true);
  }

  // Enable or Pause the ability for the user to move with mouse or keyboard
  // Movement is enabled on init
  public enablePlayerMovement(movementEnabled: boolean): void {
    if (this.mouseMovementController && !this.browserService.isMobileOrTablet) {
      this.mouseMovementController.movementEnabled = movementEnabled;
    } else {
      this.info('Movement on Mouse Controller not enabled');
    }
    if (this.sceneManager?.joystick) {
      this.sceneManager.joystick.movementEnabled = movementEnabled;
    }
  }

  public async destroy(): Promise<void> {
    try {
      await this.sceneManager?.destroy();
    } catch (e) {
      this.error('Error trying to shutdown the scene manager', e);
    }
    this.sceneManager = undefined;
    this.playersInView.clear();
    this.playersInView$.next(this.playersInView);
    // Assets.reset();
  }

  public get fps(): number {
    return this.sceneManager?.fps ?? 0;
  }

  public get currentZoom(): number {
    return this.sceneManager?.currentZoom ?? DEFAULT_ZOOM;
  }

  public get worldDimensions(): { width: number; height: number } {
    return {
      width: this.sceneManager?.viewport?.worldWidth ?? 0,
      height: this.sceneManager?.viewport?.worldHeight ?? 0,
    };
  }

  public getGroups(): Record<string, unknown>[] {
    return this.sceneManager?.groupManager?.toArray() ?? [];
  }

  /**
   * The current user's group.
   */
  public get userGroup(): number | undefined {
    return this.sceneManager?.player?.groupId;
  }

  public isFeatureAllowed(key: string): Observable<boolean> {
    const settings = this.socialSettings?.settings ?? {};
    const roomsetSettings = this.socialSettings?.setSettings ?? {};
    let allowed = true;

    // roomsetSettings take precedence over settings
    if (Object.prototype.hasOwnProperty.call(roomsetSettings, key)) {
      allowed = (roomsetSettings[key] as boolean) || this.isHost;
    } else if (Object.prototype.hasOwnProperty.call(settings, key)) {
      allowed = (settings[key] as boolean) || this.isHost;
    }
    return of(allowed);
  }

  public getSetting(key: string) {
    const settings = this.socialSettings?.settings ?? {};
    const roomsetSettings = this.socialSettings?.setSettings ?? {};
    // roomsetSettings take precedence over settings
    if (Object.prototype.hasOwnProperty.call(roomsetSettings, key)) {
      return roomsetSettings[key];
    }
    if (Object.prototype.hasOwnProperty.call(settings, key)) {
      return settings[key];
    }
    return undefined;
  }

  public showNotification(status: SnackBarType, title: string, msg: string, icon: string) {
    const _data: SnackBarData = {
      message: msg,
      customTitle: title,
      type: status,
      mode: 'light',
      icon,
      isMatIcon: true,
      iconSize: 32,
    };
    this._snackBar?.openFromComponent(SnackBarComponent, {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }

  public showGroupNotification(
    title: string,
    message: string,
    type?: SnackBarType,
    icon?: string,
    duration = 6000,
    mobileTitle?: string,
    mobileMessage = '',
  ) {
    const isMobile = this.browserService.isMobile();

    const _data: SnackBarData = {
      customTitle: isMobile ? mobileTitle : title,
      message: isMobile ? mobileMessage : message,
      type: type || '',
      icon: icon || '',
      mode: 'light',
      isMatIcon: true,
      iconSize: 32,
    };
    this._snackBar?.openFromComponent(SnackBarComponent, {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: isMobile ? 'top' : 'bottom',
      panelClass: isMobile ? 'custom-snackbar-group-mobile' : 'custom-snackbar-group',
      data: _data,
    });
  }
}
